/**
 * Farb-Helfer für den Signatur-Editor: Hex/RGB-Umrechnung und Parsen von
 * CSS-Farbwerten (rgb/rgba/hex/transparent), wie sie getComputedStyle liefert.
 */

/** Hex auf #rrggbb normalisieren (für CSS). */
export function normalizeHex(hex: string): string {
  const m = hex.replace(/^#/, "").trim();
  if (m.length === 6 && /^[0-9A-Fa-f]+$/.test(m)) return "#" + m;
  if (m.length === 3 && /^[0-9A-Fa-f]+$/.test(m)) return "#" + m[0] + m[0] + m[1] + m[1] + m[2] + m[2];
  return hex;
}

/** Prüft, ob der String ein gültiges Hex-Farbformat ist. */
export function isValidHex(hex: string): boolean {
  const m = hex.replace(/^#/, "").trim();
  return (m.length === 6 || m.length === 3) && /^[0-9A-Fa-f]+$/.test(m);
}

/** Hex #rrggbb → { r, g, b } (0–255). */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = hex.replace(/^#/, "").match(/^([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

/** r,g,b 0–255 → Hex #rrggbb. */
export function rgbToHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("");
}

export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** CSS-Farbwert (rgb/rgba/hex/transparent) → Kanäle; null wenn nicht interpretierbar. */
export function parseCssColor(value: string): RgbaColor | null {
  const v = (value || "").trim().toLowerCase();
  if (!v) return null;
  if (v === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  const m = v.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const parts = m[1]
      .split(/[\s,/]+/)
      .filter((p) => p.length > 0)
      .map((p) => parseFloat(p));
    if (parts.length < 3 || parts.slice(0, 3).some((n) => !Number.isFinite(n))) return null;
    const a = parts.length > 3 && Number.isFinite(parts[3]) ? parts[3] : 1;
    return { r: parts[0], g: parts[1], b: parts[2], a };
  }
  if (isValidHex(v)) {
    const { r, g, b } = hexToRgb(normalizeHex(v));
    return { r, g, b, a: 1 };
  }
  return null;
}

/** Kanäle → Hex (deckend) bzw. rgba(...) (transparent), passend für die Farb-Swatches. */
export function cssColorToSwatchValue(c: RgbaColor): string {
  if (c.a >= 1) return rgbToHex(c.r, c.g, c.b);
  return `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${Math.round(c.a * 100) / 100})`;
}

/** CSS-Farbwert → Hex; liefert fallback, wenn der Wert unbekannt oder transparent ist. */
export function cssColorToHex(value: string, fallback: string): string {
  const parsed = parseCssColor(value);
  if (!parsed || parsed.a === 0) return fallback;
  return rgbToHex(parsed.r, parsed.g, parsed.b);
}
